import { useSearchParams } from 'react-router-dom';
import { createContext, useCallback, useEffect, useMemo } from 'react';
import { useCoreService } from 'core/services/useCoreService';
import { SearchTransfersRequest } from 'core/models/SearchTransfersRequest';
import { Pageable } from 'shared/table/models/Pageable';
import { Transferencia } from '../models/Transferencia';
import { useDataFetcher } from 'shared/http/hooks/useDataFetcher';

export interface ITransferContext {
  searchParams: SearchTransfersRequest | null;
  transferencias: Pageable<Transferencia> | null;
  isLoading: boolean;
  searchTransfers: (request: SearchTransfersRequest) => void;
}

export const TransfersContext = createContext<ITransferContext>({
  searchParams: new SearchTransfersRequest(),
  transferencias: null,
  isLoading: true,
  searchTransfers: () => {},
});

const getFirstDayOfMonth = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const getLastDayOfMonth = () =>
  new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

export const TransfersProvider = ({ children }) => {
  const [transferencias, setRequest] = useDataFetcher<Pageable<Transferencia>>();
  const [searchParamsUrl, setSearchParamsUrl] = useSearchParams();
  const coreService = useCoreService();

  const searchParams: SearchTransfersRequest = useMemo(() => {
    const request = SearchTransfersRequest.fromURLParams(searchParamsUrl);
    const isFirstLoad = searchParamsUrl.size === 0;
    if (isFirstLoad) {
      request.from = getFirstDayOfMonth();
      request.to = getLastDayOfMonth();
    }
    return request;
  }, [searchParamsUrl]);

  useEffect(() => {
    // Si entran sin parámetros de fecha, forzamos la actualización de la URL
    if (!searchParamsUrl.has('from') || !searchParamsUrl.has('to')) {
      searchTransfers(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRequest(() => coreService.findTransferencias(searchParams));
  }, [setRequest, coreService, searchParams]);

  const searchTransfers = useCallback(
    (request: SearchTransfersRequest) => {
      const rawData = {
        estado: request.estado,
        from: request.from?.toISOString(),
        to: request.to?.toISOString(),
        cuentaOrigen: request.cuentaOrigen ? `${request.cuentaOrigen}` : null,
        cuentaDestino: request.cuentaDestino ? `${request.cuentaDestino}` : null,
        username: request.username,
      };
      // Filtramos valores nulos
      const cleanEntries = Object.entries(rawData).filter(
        ([_, value]) => value !== undefined && value !== null && value !== '',
      );
      setSearchParamsUrl(new URLSearchParams(cleanEntries));
    },
    [setSearchParamsUrl],
  );

  const value: ITransferContext = {
    searchParams,
    transferencias: transferencias.data || null,
    isLoading: transferencias.isLoading,
    searchTransfers,
  };

  return <TransfersContext.Provider value={value}>{children}</TransfersContext.Provider>;
};
